import { z } from 'zod';

import { cursor } from '@nesso/common-contracts/cursor';
import { defineQuery } from '@nesso/common-contracts/define';
import { practiceIdSchema, practiceMemberIdSchema } from '@nesso/common-contracts/ids';
import { metadata } from '@nesso/common-contracts/schema-metadata';

import { billingHistoryEntrySchema } from './billing-history.models';
import { clientIdSchema, paymentIdSchema, sessionChargeIdSchema, sessionIdSchema } from './ids';
import { paymentSchema } from './payment.models';

export const GET_SESSION_CHARGE_LIST_TYPE = 'session-charge/get-list';

export const GET_CLIENT_BALANCE_TYPE = 'session-charge/get-client-balance';

export const GET_UNPAID_SESSION_CHARGE_LIST_TYPE = 'session-charge/get-unpaid-list';

export const SESSION_CHARGE_STATUSES = ['UNPAID', 'PAID'] as const;

export const sessionChargePaymentSchema = z.object({
    paymentId: paymentIdSchema,
    number: z.number().int().min(1),
    paidAt: z.iso.datetime(),
    amountCents: z.number().int().min(1),
});

export type SessionChargePayment = z.infer<typeof sessionChargePaymentSchema>;

export const sessionChargeSchema = z.object({
    id: sessionChargeIdSchema,
    sessionId: sessionIdSchema,
    date: z.iso.datetime(),
    serviceCode: z.string(),
    feeCents: z.number().int().min(0),
    paidCents: z.number().int().min(0),
    balanceCents: z.number().int().min(0),
    status: z.enum(SESSION_CHARGE_STATUSES),
    paidAt: z.iso.datetime().nullable(),
    payments: z.array(sessionChargePaymentSchema),
    history: z.array(billingHistoryEntrySchema),
});

export type SessionCharge = z.infer<typeof sessionChargeSchema>;

export const unpaidSessionChargeSchema = sessionChargeSchema.pick({
    id: true,
    sessionId: true,
    date: true,
    serviceCode: true,
    balanceCents: true,
});

export type UnpaidSessionCharge = z.infer<typeof unpaidSessionChargeSchema>;

export const clientBalanceSchema = z.object({
    balanceCents: z.number().int().min(0),
    creditsCents: z.number().int().min(0),
    refundsCents: z.number().int().min(0),
    writeOffsCents: z.number().int().min(0),
    unpaidCount: z.number().int().min(0),
    paidCount: z.number().int().min(0),
});

export type ClientBalance = z.infer<typeof clientBalanceSchema>;

export const clientScopeSchema = z.object({
    practiceId: practiceIdSchema.meta(metadata.nonSensitive),
    practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
    clientId: clientIdSchema.meta(metadata.recordId),
});

export const getSessionChargeList = defineQuery(
    GET_SESSION_CHARGE_LIST_TYPE,
    cursor.input.extend(clientScopeSchema.shape),
    cursor.output.extend({
        items: z.array(sessionChargeSchema),
        payments: z.array(paymentSchema),
    }),
);

export const getUnpaidSessionChargeList = defineQuery(
    GET_UNPAID_SESSION_CHARGE_LIST_TYPE,
    clientScopeSchema,
    z.object({ items: z.array(unpaidSessionChargeSchema) }),
);

export const getClientBalance = defineQuery(
    GET_CLIENT_BALANCE_TYPE,
    clientScopeSchema,
    clientBalanceSchema,
);
